import React, { useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SignupForm.css';
import { useAuth } from '../../state/AuthContext';
import Field from '../common/Field/Field';
import ReCaptcha from '../common/ReCaptcha/ReCaptcha';
import { RECAPTCHA_SITE_KEY } from '../../config/env';
import { APP_ROUTES } from '../../routes/routingContract';

export default function SignupForm() {
  const { signup, error, setError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const policy = useMemo(() => evaluatePassword(password), [password]);
  const emailValid = /.+@.+\..+/.test(email.trim());
  const normalizedPhone = normalizeUSPhone(phone);
  const phoneValid = normalizedPhone !== null;
  const namesValid = firstName.trim().length > 0 && lastName.trim().length > 0;
  const passwordValid = policy.passesMin;
  const canSubmit = (emailValid || phoneValid) && namesValid && password.length >= 8 && passwordValid;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    
    // Check if CAPTCHA is required but not completed
    if (RECAPTCHA_SITE_KEY && !captchaToken) {
      setError('Please complete the CAPTCHA verification');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const payload = {
        email: email.trim() || null,
        phone: normalizedPhone,
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        password,
        captcha_token: captchaToken,
      };
      await signup(payload);
      navigate(APP_ROUTES.root, { replace: true });
    } catch (err) {
      // Generic error messages for security - don't reveal specific issues
      if (err.status === 429) {
        setError('Too many signup attempts. Please try again later.');
      } else if (err.message.includes('CAPTCHA')) {
        setError('CAPTCHA verification failed. Please try again.');
      } else {
        setError('Signup failed. Please check your information and try again.');
      }
      
      // Reset CAPTCHA on error
      if (captchaRef.current) {
        captchaRef.current.reset();
        setCaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="signup-form">
        <div className="form-header">
          <h2>Welcome to Wealth-Y</h2>
          <p className="subtitle">Create your account to start managing your financial future.</p>
        </div>

        <Field
          label="Email Address"
          name="email"
          type="email"
          value={email}
          onChange={(e)=>setEmail(e.target.value)}
          placeholder="you@mail.com"
          icon="email"
          valid={email ? emailValid : undefined}
        />
        <Field
          label="Phone"
          name="phone"
          type="tel"
          value={phone}
          onChange={(e)=>setPhone(formatUSPhone(e.target.value))}
          placeholder="(415) 555-0123"
          icon="phone"
          valid={phone ? phoneValid : undefined}
          inputMode="tel"
          autoComplete="tel"
          maxLength={14}
        />

        <div className="name-row">
          <Field
            label="First name"
            name="first_name"
            type="text"
            value={firstName}
            onChange={(e)=>setFirstName(e.target.value)}
            placeholder="First name"
            icon="user"
            valid={firstName ? true : undefined}
            showStatus
          />
          <Field
            label="Last name"
            name="last_name"
            type="text"
            value={lastName}
            onChange={(e)=>setLastName(e.target.value)}
            placeholder="Last name"
            icon="user"
            valid={lastName ? true : undefined}
            showStatus
          />
        </div>

        <Field
          label="Password"
          name="password"
          value={password}
          onChange={(e)=>setPassword(e.target.value)}
          placeholder="Create a strong password"
          icon="lock"
          withEye
          showPassword={showPassword}
          onToggleShow={() => setShowPassword(v => !v)}
          valid={password ? passwordValid : undefined}
        />
        {password && <PasswordRequirements policy={policy} />}

        <ReCaptcha 
          ref={captchaRef}
          onVerify={(token) => {
            setCaptchaToken(token);
            setError(null);
          }}
          onExpire={() => setCaptchaToken(null)}
          onError={() => setError('CAPTCHA error. Please refresh the page.')}
        />
        {error && <div className="error" role="alert">{error}</div>}
        <button type="submit" disabled={!canSubmit || loading} className="btn-primary">{loading ? 'Creating…' : 'Continue'}</button>
      </div>
    </form>
  );
}

function formatUSPhone(value) {
  const digits = value.replace(/\D/g, '');
  const usDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  const limited = usDigits.slice(0, 10);

  if (limited.length <= 3) return limited;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
}

function normalizeUSPhone(value) {
  const digits = value.replace(/\D/g, '');
  const usDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return usDigits.length === 10 ? `+1${usDigits}` : null;
}

function evaluatePassword(pw) {
  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const hasNumber = /[0-9]/.test(pw);
  const hasSpecial = /[^A-Za-z0-9]/.test(pw);
  const not3repeat = !/(.)\1\1/.test(pw);
  const categories = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
  const atLeast3 = categories >= 3;
  const lengthOk = pw.length >= 8 && pw.length <= 128;
  const passesMin = atLeast3 && not3repeat && lengthOk;
  return { hasLower, hasUpper, hasNumber, hasSpecial, not3repeat, atLeast3, lengthOk, passesMin };
}

function PasswordRequirements({ policy }) {
  const requirements = [
    { key: 'lengthOk', text: 'At least 8 characters', met: policy.lengthOk },
    { key: 'atLeast3', text: 'At least 3 of: lowercase, uppercase, number, or special character', met: policy.atLeast3 },
    { key: 'not3repeat', text: 'No more than 2 identical characters in a row', met: policy.not3repeat },
  ];

  return (
    <div className="pw-requirements" aria-live="polite">
      {requirements.map(req => (
        <div key={req.key} className={`pw-req ${req.met ? 'met' : 'unmet'}`}>
          <span className="pw-req-icon">{req.met ? '✓' : '○'}</span>
          <span className="pw-req-text">{req.text}</span>
        </div>
      ))}
    </div>
  );
}
